import React, { useState, useEffect, useRef, useMemo } from "react";
import * as Mui from "@mui/material";
import { ThemeProvider } from "@emotion/react";
import {
  designTheme,
  designColors,
  designShell,
  designType,
  designRadii,
  designWeights,
  paginationFooter,
} from "services/constants/designTokens";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import { Loader } from "components/Loader";
import sessionHandling from "services/utils/notificationUtils";
import {
  ReceiptLong,
  CheckCircle,
  HourglassTop,
  AccountBalanceWallet,
  Visibility,
  PictureAsPdf,
  Download,
  CalendarMonth,
} from "@mui/icons-material";
import { getTemplateById } from "pages/PayRoll/PayslipTemplates/templates";
import { getCompanyPayslipTemplateCode } from "services/utils/payslipTemplate";
import { BlobProvider } from "@react-pdf/renderer";
import { useHistory } from "react-router-dom";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { GlobalBreadcrumb } from "components/DesignSystem/sharedUiPatterns";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";

// The payslip feed carries no explicit status field yet, so a payslip that has
// a pay_date and a settled net_pay is treated as "Paid"; anything still awaiting
// a payout shows as "Processing".
const getPayslipStatus = (item) =>
  item?.status || (item?.pay_date ? "Paid" : "Processing");

const formatAmount = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// Payslip component rows expose their name/amount under a handful of legacy
// keys depending on which payroll cycle produced them — mirror the accessors
// the PDF template uses so the on-screen Breakdown tab stays in step with it.
const getComponentName = (item) =>
  item?.component_name ?? item?.name ?? item?.component ?? item?.label ?? "";

const getComponentAmount = (item) =>
  Number(item?.amount ?? item?.component_amount ?? item?.value ?? item?.total ?? 0);

const normalizeItems = (...sections) =>
  sections
    .flatMap((section) => (Array.isArray(section) ? section : []))
    .filter((item) => item && getComponentName(item));

const sumAmounts = (items) =>
  items.reduce((sum, item) => sum + (getComponentAmount(item) || 0), 0);

const payslipHeaders = [
  {
    key: "pay_period",
    name: "Pay Period",
    type: "string",
    icon: <ReceiptLong fontSize="small" />,
  },
  {
    key: "pay_date",
    name: "Pay Date",
    type: "date",
    icon: <CalendarMonth fontSize="small" />,
  },
  {
    key: "net_pay",
    name: "Net Pay",
    type: "number",
    icon: <AccountBalanceWallet fontSize="small" />,
  },
  {
    key: "status",
    name: "Status",
    type: "autocomplete",
    icon: <CheckCircle fontSize="small" />,
  },
];

const normalizePayslipRow = (item = {}) => ({
  ...item,
  pay_period:
    item?.start_date && item?.end_date
      ? `${item.start_date} to ${item.end_date}`
      : "-",
  status: getPayslipStatus(item),
  net_pay: Number(item?.net_pay || 0),
});

const PayslipList = ({ subModuleId }) => {
  const history = useHistory();
  const { domain, token, userDetails, companyDetails, topbarCompanyId } =
    useSelector((state) => state.tokenReducer);
  const activeTemplateId = useSelector(
    (state) => state.userReducer.payslipTemplateId
  );
  // The applied template comes from the company list's payslip_template_code.
  // Resolve it from the payslip's own company so an employee always gets their
  // company's configured format; the cached Redux id (only refreshed on a
  // company switch or a Payroll Setup visit) is the last fallback.
  const getSingleDocument = (payslip) => {
    const code =
      getCompanyPayslipTemplateCode(companyDetails, {
        companyId: payslip?.company_id ?? payslip?.company,
        companyName: payslip?.company_name,
      }) ||
      getCompanyPayslipTemplateCode(companyDetails, {
        companyId: topbarCompanyId,
      }) ||
      activeTemplateId;
    return getTemplateById(code).SingleDocument;
  };

  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [payslipsList, setPayslipsList] = useState([]);
  const [pslipOpenDialog, setPslipOpenDialog] = useState(false);
  const [indPayslipData, setIndPayslipData] = useState(null);
  const [activeRow, setActiveRow] = useState(null);
  const [drawerTab, setDrawerTab] = useState(0);
  const [masterDataForSearch, setMasterDataForSearch] = useState([]);
  const [pdfKey, setPdfKey] = useState(0);
  const [downloadTarget, setDownloadTarget] = useState(null);
  const iframeRef = useRef(null);
  const SingleDocument = getSingleDocument(indPayslipData);
  const DownloadDocument = getSingleDocument(downloadTarget);

  useEffect(() => {
    const fetchUserPayslipDetails = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(
          `${domain}payroll/operations/payrollprocessing/payrollruncycle/user-payslip-details/?user_id=${userDetails?.id}&company_id=${topbarCompanyId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          }
        );
        const res = await response.json();
        if (response.ok) {
          setMasterDataForSearch(res);
        } else if (response.status === 409) {
          setMasterDataForSearch([]);
          sessionHandling();
        } else {
          setMasterDataForSearch([]);
          throw new Error("REQUEST_FAILED");
        }
      } catch (error) {
        console.error("[Paysliplist] operation failed:", error);
        setMasterDataForSearch([]);
        showToast("error", "Couldn't load your payslip. Please refresh the page.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchUserPayslipDetails();
  }, [domain, token, userDetails, topbarCompanyId]);

  const filterInputRows = useMemo(
    () => (masterDataForSearch || []).map(normalizePayslipRow),
    [masterDataForSearch],
  );

  useEffect(() => {
    setPayslipsList(filterInputRows);
    setPage(0);
  }, [filterInputRows]);

  const handleFilteredDataChange = (filteredRows = []) => {
    setPayslipsList(filteredRows);
    setPage(0);
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const summary = useMemo(() => {
    const list = payslipsList || [];
    const paidCount = list.filter(
      (item) => getPayslipStatus(item) === "Paid"
    ).length;
    const processingCount = list.filter(
      (item) => getPayslipStatus(item) === "Processing"
    ).length;
    const totalNetPay = list.reduce(
      (sum, item) => sum + Number(item?.net_pay || 0),
      0
    );
    const pct = (count) =>
      list.length === 0 ? "0.0" : ((count / list.length) * 100).toFixed(1);
    return {
      total: list.length,
      paid: paidCount,
      processing: processingCount,
      totalNetPay,
      paidPct: pct(paidCount),
      processingPct: pct(processingCount),
    };
  }, [payslipsList]);

  const summaryCards = [
    {
      label: "Total Payslips",
      value: summary.total,
      subtext: "In selected period",
      icon: <ReceiptLong />,
      iconColor: "#1D4ED8",
      iconBg: "#EEF4FF",
    },
    {
      label: "Paid",
      value: summary.paid,
      subtext: `${summary.paidPct}%`,
      icon: <CheckCircle />,
      iconColor: "#16A34A",
      iconBg: designColors.tealTint,
    },
    {
      label: "Processing",
      value: summary.processing,
      subtext: `${summary.processingPct}%`,
      icon: <HourglassTop />,
      iconColor: "#D97706",
      iconBg: "#FEF3E7",
    },
    {
      label: "Total Net Pay",
      value: formatAmount(summary.totalNetPay),
      subtext: "In selected period",
      icon: <AccountBalanceWallet />,
      iconColor: designColors.tealDark,
      iconBg: designColors.tealTint,
    },
  ];

  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "myzone_payslip_saved_views",
    fullHeaders: payslipHeaders,
    screenAccess: true,
    onViewChange: () => setPage(0),
  });

  const getPayslipData = async (payschle) => {
    const response = await fetch(
      `${domain}payroll/operations/payrollprocessing/payrollruncycle/get-payslip-details/?user_id=${payschle?.user_id}&pay_schedule_id=${payschle?.pay_schedule_id}&company_id=${topbarCompanyId}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `token ${token}`,
        },
      }
    );
    const jsonData = await response.json();
    if (response.ok) {
      return jsonData;
    } else if (response.status === 409) {
      sessionHandling();
      return null;
    }
    throw new Error(jsonData?.error);
  };

  const handlePreview = async (payschle) => {
    try {
      const jsonData = await getPayslipData(payschle);
      if (!jsonData) return;
      setIndPayslipData(jsonData);
      setActiveRow(payschle);
      setDrawerTab(0);
      setPslipOpenDialog(true);
      setPdfKey((prev) => prev + 1);
    } catch (error) {
      console.error("[Paysliplist] operation failed:", error);
      showToast("error", "Couldn't open the payslip. Please try again.");
      setIndPayslipData(null);
      setPslipOpenDialog(false);
    }
  };

  // Direct row download: fetch the detail payload, then hand it to a hidden
  // BlobProvider (see below) which auto-clicks the download once the PDF blob
  // is ready. Keeps the "PDF" row action a one-click action without forcing the
  // preview drawer open first.
  const handleDownload = async (payschle) => {
    try {
      const jsonData = await getPayslipData(payschle);
      if (!jsonData) return;
      setDownloadTarget(jsonData);
      setPdfKey((prev) => prev + 1);
    } catch (error) {
      console.error("[Paysliplist] operation failed:", error);
      showToast("error", "Couldn't download the payslip. Please try again.");
      setDownloadTarget(null);
    }
  };

  const triggerBlobDownload = (url, data) => {
    if (!url) return;
    const link = document.createElement("a");
    link.href = url;
    link.download = `${
      (data?.user_name || "") + (data?.pay_schedule_name || "") || "payslip"
    }.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleDrawer = () => {
    setPslipOpenDialog(false);
    setIndPayslipData(null);
    setActiveRow(null);
    if (iframeRef.current) {
      iframeRef.current.src = "";
    }
  };

  // Earnings / deductions view model for the drawer's Breakdown tab.
  const breakdown = useMemo(() => {
    if (!indPayslipData) return null;
    const earnings = normalizeItems(
      indPayslipData?.earnings,
      indPayslipData?.other_earnings
    );
    const deductions = normalizeItems(
      indPayslipData?.deduction,
      indPayslipData?.deductions,
      indPayslipData?.other_deduction,
      indPayslipData?.other_deductions,
      indPayslipData?.additional_deduction,
      indPayslipData?.additional_deductions
    );
    const totalEarnings =
      Number(indPayslipData?.total_earnings) || sumAmounts(earnings);
    const totalDeductions =
      Number(
        indPayslipData?.total_deductions ?? indPayslipData?.total_deduction
      ) || sumAmounts(deductions);
    const netPay =
      Number(indPayslipData?.net_pay ?? indPayslipData?.net_salary) ||
      Math.max(totalEarnings - totalDeductions, 0);
    return { earnings, deductions, totalEarnings, totalDeductions, netPay };
  }, [indPayslipData]);

  const drawerTitle = activeRow?.pay_schedule_name || "Payslip";

  const renderPayslipCell = (item, key) => {
    switch (key) {
      case "pay_period":
        return item?.pay_period || "-";
      case "pay_date":
        return item?.pay_date || "-";
      case "net_pay":
        return formatAmount(item?.net_pay);
      case "status": {
        const status = getPayslipStatus(item);
        const isPaid = status === "Paid";
        return (
          <Mui.Chip
            label={status}
            size="small"
            sx={{
              height: 24,
              fontSize: 12,
              fontWeight: designWeights.semibold,
              borderRadius: designRadii.pill,
              color: isPaid ? designColors.tealDark : "#B45309",
              backgroundColor: isPaid ? designColors.tealTint : "#FEF3E7",
              border: `1px solid ${isPaid ? "#B7E4DB" : "#F6D9AE"}`,
            }}
          />
        );
      }
      default:
        return item?.[key] || "-";
    }
  };

  const PayslipMobileCard = ({ item }) => (
    <Mui.Box
      sx={{
        border: `1px solid ${designColors.slate200}`,
        borderRadius: designRadii.card,
        p: 1.5,
        backgroundColor: designColors.white,
      }}
    >
      <Mui.Stack direction="row" justifyContent="space-between" spacing={1.5} alignItems="flex-start">
        <Mui.Box sx={{ minWidth: 0 }}>
          <Mui.Typography sx={{ fontSize: 13, fontWeight: designWeights.heading, color: designColors.slate900 }}>
            {renderPayslipCell(item, "pay_period")}
          </Mui.Typography>
          <Mui.Typography sx={{ mt: 0.4, fontSize: 12, color: designColors.slate500 }}>
            Pay Date: {renderPayslipCell(item, "pay_date")}
          </Mui.Typography>
        </Mui.Box>
        {renderPayslipCell(item, "status")}
      </Mui.Stack>
      <Mui.Box
        sx={{
          mt: 1.5,
          pt: 1.25,
          borderTop: `1px solid ${designColors.slate200}`,
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 1.25,
        }}
      >
        <Mui.Box>
          <Mui.Typography sx={{ fontSize: 10.5, fontWeight: designWeights.semibold, color: designColors.slate500 }}>
            Net Pay
          </Mui.Typography>
          <Mui.Typography sx={{ mt: 0.25, fontSize: 13, fontWeight: designWeights.semibold, color: designColors.slate900 }}>
            {renderPayslipCell(item, "net_pay")}
          </Mui.Typography>
        </Mui.Box>
      </Mui.Box>
      <Mui.Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
        <Mui.Button
          fullWidth
          size="small"
          startIcon={<Visibility sx={{ fontSize: 18 }} />}
          onClick={() => handlePreview(item)}
          sx={{ ...designShell.compactOutlineButton, minHeight: 34 }}
        >
          Preview
        </Mui.Button>
        <Mui.Button
          fullWidth
          size="small"
          startIcon={<PictureAsPdf sx={{ fontSize: 18 }} />}
          onClick={() => handleDownload(item)}
          sx={{ ...designShell.compactOutlineButton, minHeight: 34, color: designColors.slate700 }}
        >
          PDF
        </Mui.Button>
      </Mui.Stack>
    </Mui.Box>
  );

  return (
    <ThemeProvider theme={designTheme}>
      <Mui.Box sx={{ width: "100%", minHeight: "100%", p: { xs: 1.5, md: 3 } }}>
        <GlobalBreadcrumb
          sx={{ mb: 1.5 }}
          trail={[
            {
              label: "My Workspace",
              onClick: () => history.push("/admin/myzone/status"),
            },
            "Payslips",
          ]}
        />
        {/* Page header */}
        <Mui.Box sx={{ mt: 1 }}>
          <Mui.Typography sx={designType.pageTitle}>Payslips</Mui.Typography>
          <Mui.Typography sx={designType.pageSubtitle}>
            View and download your payslips generated by payroll.
          </Mui.Typography>
        </Mui.Box>

        {isLoading ? (
          <Loader info="Loading..." />
        ) : (
          <>
            {/* Summary cards */}
            <Mui.Grid container spacing={2} sx={{ mt: 2 }}>
              {summaryCards.map((card) => (
                <Mui.Grid item xs={12} sm={6} md={3} key={card.label}>
                  <Mui.Paper elevation={0} sx={{ ...designShell.sectionCard, p: 2 }}>
                    <Mui.Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                    >
                      <Mui.Avatar
                        variant="rounded"
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: "12px",
                          bgcolor: card.iconBg,
                          color: card.iconColor,
                        }}
                      >
                        {card.icon}
                      </Mui.Avatar>
                      <Mui.Box sx={{ minWidth: 0 }}>
                        <Mui.Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: designWeights.semibold,
                            color: designColors.slate600,
                          }}
                        >
                          {card.label}
                        </Mui.Typography>
                        <Mui.Typography
                          sx={{
                            fontSize: 22,
                            fontWeight: designWeights.heading,
                            color: designColors.slate900,
                            lineHeight: 1.2,
                          }}
                        >
                          {card.value}
                        </Mui.Typography>
                        <Mui.Typography
                          sx={{ fontSize: 12, color: designColors.slate500 }}
                        >
                          {card.subtext}
                        </Mui.Typography>
                      </Mui.Box>
                    </Mui.Stack>
                  </Mui.Paper>
                </Mui.Grid>
              ))}
            </Mui.Grid>

            {/* Table */}
            <Mui.Paper
              elevation={0}
              sx={{ ...designShell.sectionCard, ...designShell.tableCard, mt: 2 }}
            >
              <CustomFilter
                title="Payslip History"
                titleOverride={titleOverride}
                filterHeaders={visibleHeaders}
                data={filterInputRows}
                filteredData={payslipsList}
                onFilteredDataChange={handleFilteredDataChange}
                screenAccess={true}
                showSaveButton={true}
                hideCount={true}
                downloadFormats={["excel", "pdf", "csv"]}
                includeMetadata={true}
                metadata={{
                  "User Name":
                    userDetails?.username ||
                    userDetails?.user_name ||
                    userDetails?.first_name ||
                    "N/A",
                  "Report Generated On": new Date().toLocaleString(),
                }}
                labeledToolbar={true}
                searchPlaceholder="Search payslips..."
              />
              <Mui.Box sx={{ display: { xs: "grid", sm: "none" }, gap: 1.25 }}>
                {payslipsList?.length === 0 ? (
                  <Mui.Box
                    sx={{
                      py: 4,
                      textAlign: "center",
                      color: designColors.slate500,
                      border: `1px dashed ${designColors.slate200}`,
                      borderRadius: designRadii.card,
                    }}
                  >
                    No payslips found.
                  </Mui.Box>
                ) : (
                  payslipsList
                    ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((item, index) => (
                      <PayslipMobileCard key={item?.id ?? index} item={item} />
                    ))
                )}
              </Mui.Box>
              <Mui.TableContainer sx={{ display: { xs: "none", sm: "block" } }}>
                <Mui.Table>
                  <Mui.TableHead sx={designShell.tableStickyHeadRow}>
                    <Mui.TableRow>
                      {visibleHeaders.map((head) => (
                        <Mui.TableCell
                          key={head.key}
                          sx={{
                            ...designShell.tableHeadCellBase,
                            fontWeight: designWeights.semibold,
                            py: 1.25,
                          }}
                        >
                          {head.name}
                        </Mui.TableCell>
                      ))}
                      <Mui.TableCell
                        align="right"
                        sx={{
                          ...designShell.tableHeadCellBase,
                          fontWeight: designWeights.semibold,
                          py: 1.25,
                        }}
                      >
                        Actions
                      </Mui.TableCell>
                    </Mui.TableRow>
                  </Mui.TableHead>
                  <Mui.TableBody>
                    {payslipsList?.length === 0 ? (
                      <TableEmptyState
                        colSpan={(visibleHeaders?.length || 0) + 1}
                        dataLength={masterDataForSearch}
                      />
                    ) : (
                      payslipsList
                        ?.slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage
                        )
                        .map((item, index) => {
                          return (
                            <Mui.TableRow
                              key={index}
                              hover
                              sx={designShell.tableRowHover}
                            >
                              {visibleHeaders.map((head) => (
                                <Mui.TableCell
                                  key={head.key}
                                  sx={{
                                    py: 1.25,
                                    fontSize: 14,
                                    color:
                                      head.key === "net_pay"
                                        ? designColors.slate900
                                        : designColors.slate700,
                                    fontWeight:
                                      head.key === "net_pay"
                                        ? designWeights.semibold
                                        : designWeights.regular,
                                    borderBottom: `1px solid ${designColors.slate200}`,
                                  }}
                                >
                                  {renderPayslipCell(item, head.key)}
                                </Mui.TableCell>
                              ))}
                              <Mui.TableCell
                                align="right"
                                sx={{
                                  py: 1,
                                  borderBottom: `1px solid ${designColors.slate200}`,
                                }}
                              >
                                <Mui.Stack
                                  direction="row"
                                  spacing={0.5}
                                  justifyContent="flex-end"
                                >
                                  <Mui.Button
                                    size="small"
                                    startIcon={<Visibility sx={{ fontSize: 18 }} />}
                                    onClick={() => handlePreview(item)}
                                    sx={{
                                      ...designShell.compactOutlineButton,
                                      minHeight: 32,
                                      px: 1.25,
                                    }}
                                  >
                                    Preview
                                  </Mui.Button>
                                  <Mui.Button
                                    size="small"
                                    startIcon={<PictureAsPdf sx={{ fontSize: 18 }} />}
                                    onClick={() => handleDownload(item)}
                                    sx={{
                                      ...designShell.compactOutlineButton,
                                      minHeight: 32,
                                      px: 1.25,
                                      color: designColors.slate700,
                                    }}
                                  >
                                    PDF
                                  </Mui.Button>
                                </Mui.Stack>
                              </Mui.TableCell>
                            </Mui.TableRow>
                          );
                        })
                    )}
                  </Mui.TableBody>
                </Mui.Table>
              </Mui.TableContainer>
              {payslipsList.length > 0 && (
                <Mui.Box
                  sx={{
                    px: 2,
                    py: 1.25,
                    borderTop: `1px solid ${designColors.slate200}`,
                  }}
                >
                  {paginationFooter({
                    page,
                    rowsPerPage,
                    total: payslipsList.length,
                    itemLabel: "payslips",
                    onPageChange: handleChangePage,
                    onRowsPerPageChange: handleChangeRowsPerPage,
                    rowsPerPageOptions: [10, 20, 50, 100],
                  })}
                </Mui.Box>
              )}
            </Mui.Paper>
          </>
        )}

        {/* Hidden auto-download for the row "PDF" action */}
        {downloadTarget && (
          <BlobProvider
            key={`dl-${pdfKey}`}
            document={<DownloadDocument payslipData={downloadTarget} />}
          >
            {({ url, loading }) => {
              if (!loading && url) {
                triggerBlobDownload(url, downloadTarget);
                // Clear after the click so a later download of the same row
                // re-renders instead of being skipped as unchanged.
                setTimeout(() => setDownloadTarget(null), 0);
              }
              return null;
            }}
          </BlobProvider>
        )}

        {/* Preview panel */}
        {pslipOpenDialog && indPayslipData && (
          <GlobalSidePanel
            open={pslipOpenDialog}
            onClose={toggleDrawer}
            bodySx={{ backgroundColor: designColors.pageBg }}
            header={
              <Mui.Box sx={{ flex: 1, minWidth: 0 }}>
                <Mui.Typography sx={designType.panelTitle}>
                  {drawerTitle}
                </Mui.Typography>
                <Mui.Chip
                  label={getPayslipStatus(activeRow)}
                  size="small"
                  sx={{
                    mt: 0.75,
                    height: 24,
                    fontSize: 12,
                    fontWeight: designWeights.semibold,
                    borderRadius: designRadii.pill,
                    color: designColors.tealDark,
                    backgroundColor: designColors.tealTint,
                    border: `1px solid #B7E4DB`,
                  }}
                />

                {/* Meta grid */}
                <Mui.Grid container spacing={2} sx={{ mt: 0.5 }}>
                  {[
                    {
                      label: "Pay Period",
                      value:
                        activeRow?.start_date && activeRow?.end_date
                          ? `${activeRow.start_date} - ${activeRow.end_date}`
                          : "-",
                    },
                    { label: "Pay Date", value: activeRow?.pay_date || "-" },
                    {
                      label: "Payroll Run Date",
                      value:
                        indPayslipData?.payroll_run_date ||
                        activeRow?.pay_date ||
                        "-",
                    },
                  ].map((meta) => (
                    <Mui.Grid item xs={4} key={meta.label}>
                      <Mui.Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: designWeights.semibold,
                          color: designColors.slate500,
                        }}
                      >
                        {meta.label}
                      </Mui.Typography>
                      <Mui.Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: designWeights.medium,
                          color: designColors.slate800,
                        }}
                      >
                        {meta.value}
                      </Mui.Typography>
                    </Mui.Grid>
                  ))}
                </Mui.Grid>

                  <Mui.Tabs
                    value={drawerTab}
                    onChange={(e, v) => setDrawerTab(v)}
                    sx={designShell.panelTabs}
                  >
                  <Mui.Tab label="Payslip Preview" />
                  <Mui.Tab label="Breakdown" />
                </Mui.Tabs>
              </Mui.Box>
            }
            footer={
              <Mui.Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1.5,
                  width: "100%",
                }}
              >
                <BlobProvider
                  key={`footer-${pdfKey}`}
                  document={<SingleDocument payslipData={indPayslipData} />}
                >
                  {({ url, loading }) => (
                    <Mui.Button
                      fullWidth
                      disabled={loading || !url}
                      startIcon={<Download />}
                      onClick={() => triggerBlobDownload(url, indPayslipData)}
                      sx={designShell.primaryButtonBase}
                    >
                      Download Payslip (PDF)
                    </Mui.Button>
                  )}
                </BlobProvider>
                <Mui.Button
                  variant="outlined"
                  onClick={toggleDrawer}
                  sx={designShell.panelCloseButton}
                >
                  Close
                </Mui.Button>
              </Mui.Box>
            }
          >
            {/* Panel body */}
            <Mui.Box>
              {drawerTab === 0 ? (
                  <BlobProvider
                    key={pdfKey}
                    document={<SingleDocument payslipData={indPayslipData} />}
                  >
                    {({ url, loading }) => {
                      if (loading) {
                        return <Loader info="Generating PDF..." />;
                      }
                      if (url) {
                        return (
                          <iframe
                            ref={iframeRef}
                            src={url}
                            style={{
                              width: "100%",
                              height: "100%",
                              minHeight: "calc(100vh - 220px)",
                              border: "none",
                            }}
                            title="Payslip Preview"
                          />
                        );
                      }
                      return null;
                    }}
                  </BlobProvider>
                ) : (
                  <Mui.Box sx={{ p: 3 }}>
                    <BreakdownSection
                      title="Earnings"
                      items={breakdown?.earnings}
                      total={breakdown?.totalEarnings}
                      totalColor={designColors.tealDark}
                      totalBg={designColors.tealTint}
                    />
                    <Mui.Box sx={{ mt: 2 }}>
                      <BreakdownSection
                        title="Deductions"
                        items={breakdown?.deductions}
                        total={breakdown?.totalDeductions}
                        totalColor="#B45309"
                        totalBg="#FEF3E7"
                      />
                    </Mui.Box>
                    <Mui.Box
                      sx={{
                        mt: 2,
                        p: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderRadius: designRadii.card,
                        backgroundColor: designColors.white,
                        border: `1px solid ${designColors.slate200}`,
                      }}
                    >
                      <Mui.Typography
                        sx={{
                          fontSize: 15,
                          fontWeight: designWeights.heading,
                          color: designColors.tealDark,
                        }}
                      >
                        NET PAY
                      </Mui.Typography>
                      <Mui.Typography
                        sx={{
                          fontSize: 18,
                          fontWeight: designWeights.heading,
                          color: designColors.tealDark,
                        }}
                      >
                        {formatAmount(breakdown?.netPay)}
                      </Mui.Typography>
                    </Mui.Box>
                  </Mui.Box>
                )}
            </Mui.Box>
          </GlobalSidePanel>
        )}
      </Mui.Box>
    </ThemeProvider>
  );
};

// One earnings/deductions block for the drawer's Breakdown tab: a labelled card
// with a row per component and a coloured total bar at the foot.
const BreakdownSection = ({ title, items, total, totalColor, totalBg }) => (
  <Mui.Box
    sx={{
      borderRadius: designRadii.card,
      border: `1px solid ${designColors.slate200}`,
      overflow: "hidden",
      backgroundColor: designColors.white,
    }}
  >
    <Mui.Box
      sx={{
        px: 2,
        py: 1.25,
        backgroundColor: designColors.slate100,
        borderBottom: `1px solid ${designColors.slate200}`,
      }}
    >
      <Mui.Typography
        sx={{ fontSize: 13, fontWeight: designWeights.heading, color: designColors.slate800 }}
      >
        {title}
      </Mui.Typography>
    </Mui.Box>
    {items && items.length > 0 ? (
      items.map((item, index) => (
        <Mui.Box
          key={`${getComponentName(item)}-${index}`}
          sx={{
            px: 2,
            py: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `1px solid ${designColors.slate100}`,
          }}
        >
          <Mui.Typography sx={{ fontSize: 13, color: designColors.slate700 }}>
            {getComponentName(item)}
          </Mui.Typography>
          <Mui.Typography
            sx={{ fontSize: 13, fontWeight: designWeights.medium, color: designColors.slate900 }}
          >
            {formatAmount(getComponentAmount(item))}
          </Mui.Typography>
        </Mui.Box>
      ))
    ) : (
      <Mui.Box sx={{ px: 2, py: 1.5 }}>
        <Mui.Typography sx={{ fontSize: 13, color: designColors.slate500 }}>
          No {title.toLowerCase()} recorded.
        </Mui.Typography>
      </Mui.Box>
    )}
    <Mui.Box
      sx={{
        px: 2,
        py: 1.25,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        backgroundColor: totalBg,
      }}
    >
      <Mui.Typography sx={{ fontSize: 13, fontWeight: designWeights.heading, color: totalColor }}>
        Total {title}
      </Mui.Typography>
      <Mui.Typography sx={{ fontSize: 13, fontWeight: designWeights.heading, color: totalColor }}>
        {formatAmount(total)}
      </Mui.Typography>
    </Mui.Box>
  </Mui.Box>
);

export default PayslipList;
